import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DEAL_TYPES, type DealType } from "@colab/shared";
import { useSubmitDeal } from "./use-submit-deal";

// Phase 7 sub-bucket 5 — partner submits a new lead.
//
// Replaces /app/portal/forms/deal-registration as a dedicated lead-entry
// surface for the SPA. No auto-save (per bucket 9 invariant — auto-save
// belongs to MDF drafts only). Pessimistic submit. On success, navigates
// to /portal/deals/${id} so the partner lands on the detail view of their
// just-submitted lead.

type FormState = {
  title: string;
  deal_type: DealType;
  prospect_company: string;
  prospect_contact_name: string;
  prospect_contact_email: string;
  estimated_value_eur: string;
  notes: string;
};

const EMPTY: FormState = {
  title: "",
  deal_type: "referral",
  prospect_company: "",
  prospect_contact_name: "",
  prospect_contact_email: "",
  estimated_value_eur: "",
  notes: "",
};

export function DealNewPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(EMPTY);
  const submit = useSubmitDeal();

  function patch<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((s) => ({ ...s, [key]: value }));
  }

  const canSubmit =
    form.title.trim().length > 0 && form.prospect_company.trim().length > 0;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    submit.mutate(
      {
        title: form.title.trim(),
        deal_type: form.deal_type,
        prospect_company: form.prospect_company.trim(),
        prospect_contact_name: form.prospect_contact_name.trim() || null,
        prospect_contact_email: form.prospect_contact_email.trim() || null,
        estimated_value_eur: form.estimated_value_eur.trim()
          ? Number(form.estimated_value_eur)
          : null,
        notes: form.notes.trim() || null,
      },
      {
        onSuccess: ({ id }) => {
          toast.success("Lead submitted");
          navigate(`/portal/deals/${id}`, { replace: true });
        },
      },
    );
  }

  return (
    <div>
      <Link
        to="/portal/deals"
        className="mb-6 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3 w-3" />
        Back to deals
      </Link>

      <header className="mb-8">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Partnership
        </p>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
          Submit a lead
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Send Bookboost a new lead or partnership opportunity for review.
        </p>
      </header>

      <form onSubmit={handleSubmit} className="rounded-xl border bg-card p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Title" required>
            <Input
              value={form.title}
              onChange={(e) => patch("title", e.target.value)}
              placeholder="Short name for this lead"
              required
            />
          </Field>
          <Field label="Type">
            <Select
              value={form.deal_type}
              onValueChange={(v) => patch("deal_type", v as DealType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DEAL_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {formatDealType(t)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Customer company" required>
            <Input
              value={form.prospect_company}
              onChange={(e) => patch("prospect_company", e.target.value)}
              placeholder="Customer's company name"
              required
            />
          </Field>
          <Field label="Estimated value (EUR)">
            <Input
              inputMode="numeric"
              pattern="[0-9]*"
              value={form.estimated_value_eur}
              onChange={(e) =>
                patch(
                  "estimated_value_eur",
                  e.target.value.replace(/[^0-9]/g, ""),
                )
              }
              placeholder="0"
            />
          </Field>
          <Field label="Contact name (optional)">
            <Input
              value={form.prospect_contact_name}
              onChange={(e) => patch("prospect_contact_name", e.target.value)}
            />
          </Field>
          <Field label="Contact email (optional)">
            <Input
              type="email"
              value={form.prospect_contact_email}
              onChange={(e) => patch("prospect_contact_email", e.target.value)}
            />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Notes">
            <Textarea
              value={form.notes}
              onChange={(e) => patch("notes", e.target.value)}
              rows={4}
              placeholder="Context for the Bookboost team."
            />
          </Field>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2 border-t pt-4">
          <Button asChild variant="ghost" type="button">
            <Link to="/portal/deals">Cancel</Link>
          </Button>
          <Button type="submit" disabled={!canSubmit || submit.isPending}>
            {submit.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            Submit lead for review
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
        {required ? <span className="ml-1 text-destructive">*</span> : null}
      </Label>
      {children}
    </div>
  );
}

function formatDealType(t: string): string {
  switch (t) {
    case "referral":
      return "Referral";
    case "co_sell":
      return "Co-sell";
    case "reseller":
      return "Reseller";
    default:
      return t;
  }
}
